import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  EnvelopeIcon,
  HandRaisedIcon,
  ClockIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { IndividualMemberDto, MemberVignetteRecord, VignetteStatus } from '../../types/member';
import { adminService } from '../../services/admin';
import { resolveVignetteStatus } from '../../utils/vignette';
import { VignetteStatusBadge } from './VignetteStatusBadge';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';

const Panel = styled(Card)<{ $embedded?: boolean }>`
  ${props =>
    props.$embedded &&
    `
    box-shadow: none;
    border: none;
    background: transparent;
    padding: 0 !important;
  `}
`;

const Intro = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[5]};
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]} ${spacing[4]};
  margin-bottom: ${spacing[5]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const SectionLabel = styled(Text)`
  display: block;
  margin-bottom: ${spacing[3]};
  font-weight: ${typography.fontWeight.semibold};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[5]};
`;

const OptionCard = styled.button<{ $selected: boolean; $tone: 'muted' | 'primary' | 'success' }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  text-align: left;
  padding: ${spacing[4]};
  border-radius: ${borders.radius.md};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
  transition: ${animations.transition(
    ['border-color', 'background', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  border: 1.5px solid
    ${props => {
      if (!props.$selected) return props.theme.colors.border;
      if (props.$tone === 'success') return props.theme.colors.success;
      if (props.$tone === 'primary') return props.theme.colors.primary;
      return props.theme.colors.textSecondary;
    }};
  background: ${props => {
    if (!props.$selected) return props.theme.colors.surface;
    if (props.$tone === 'success') return `${props.theme.colors.success}12`;
    if (props.$tone === 'primary') return `${props.theme.colors.primary}12`;
    return props.theme.colors.surfaceHover;
  }};
  box-shadow: ${props =>
    props.$selected ? `0 0 0 1px ${props.theme.colors.primary}22` : 'none'};

  &:hover {
    border-color: ${props =>
      props.$selected
        ? undefined
        : props.theme.colors.primary};
    background: ${props =>
      props.$selected ? undefined : props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const OptionIcon = styled.span<{ $tone: 'muted' | 'primary' | 'success'; $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${borders.radius.md};
  background: ${props => {
    if (props.$tone === 'success') return `${props.theme.colors.success}18`;
    if (props.$tone === 'primary') return `${props.theme.colors.primary}18`;
    return props.theme.colors.surfaceHover;
  }};
  color: ${props => {
    if (props.$tone === 'success') return props.theme.colors.success;
    if (props.$tone === 'primary') return props.theme.colors.primary;
    return props.theme.colors.textSecondary;
  }};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const OptionTitle = styled.span`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const OptionHint = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

const Radio = styled.span<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${props => (props.$selected ? props.theme.colors.primary : props.theme.colors.border)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => (props.$selected ? props.theme.colors.primary : 'transparent')};
  }
`;

const TrackingField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[5]};
  padding: ${spacing[4]};
  border-radius: ${borders.radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
`;

const TrackingInput = styled.input`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.surface};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  padding-top: ${spacing[2]};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

interface AdminVignettePanelProps {
  memberId: string;
  member: IndividualMemberDto;
  onUpdated: (member: IndividualMemberDto) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  embedded?: boolean;
}

export const AdminVignettePanel: React.FC<AdminVignettePanelProps> = ({
  memberId,
  member,
  onUpdated,
  onError,
  onSuccess,
  embedded = false,
}) => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const currentStatus = resolveVignetteStatus(member);
  const [year, setYear] = useState(currentYear);
  const [history, setHistory] = useState<MemberVignetteRecord[]>([]);
  const [status, setStatus] = useState<VignetteStatus>(resolveVignetteStatus(member));
  const [trackingNumber, setTrackingNumber] = useState(member.vignetteTrackingNumber ?? '');
  const [deliveredAt, setDeliveredAt] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedRecord = history.find(row => row.year === year);

  const applyRecord = (record: MemberVignetteRecord | undefined, fallbackMember: IndividualMemberDto) => {
    if (record) {
      setStatus(record.status);
      setTrackingNumber(record.trackingNumber ?? '');
      setDeliveredAt(record.deliveredAt ? record.deliveredAt.slice(0, 10) : '');
      return;
    }
    if (year === currentYear) {
      setStatus(resolveVignetteStatus(fallbackMember));
      setTrackingNumber(fallbackMember.vignetteTrackingNumber ?? '');
      setDeliveredAt('');
      return;
    }
    setStatus(VignetteStatus.NotSent);
    setTrackingNumber('');
    setDeliveredAt('');
  };

  useEffect(() => {
    let cancelled = false;
    adminService.getMemberVignettes(memberId).then(rows => {
      if (!cancelled) setHistory(rows);
    }).catch(() => {
      if (!cancelled) setHistory([]);
    });
    return () => {
      cancelled = true;
    };
  }, [memberId, member.vignetteUpdatedAt]);

  useEffect(() => {
    applyRecord(history.find(row => row.year === year), member);
    // Reload the form when the selected year or saved history changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, history, member]);

  const baselineStatus = selectedRecord?.status ?? (year === currentYear ? resolveVignetteStatus(member) : VignetteStatus.NotSent);
  const baselineTracking = selectedRecord?.trackingNumber ?? (year === currentYear ? member.vignetteTrackingNumber ?? '' : '');
  const baselineDelivered = selectedRecord?.deliveredAt ? selectedRecord.deliveredAt.slice(0, 10) : '';
  const dirty =
    status !== baselineStatus ||
    (status === VignetteStatus.SentByMail ? trackingNumber.trim() !== (baselineTracking ?? '').trim() : false) ||
    (status !== VignetteStatus.NotSent && deliveredAt !== baselineDelivered);

  const formatUpdatedAt = (value?: string) => {
    if (!value) return t('vignette.neverUpdated');
    return new Date(value).toLocaleString(
      i18n.language.startsWith('el') || i18n.language === 'gr' ? 'el-GR' : 'en-GB',
      { dateStyle: 'medium', timeStyle: 'short' }
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminService.updateVignette(memberId, {
        vignetteStatus: status,
        year,
        vignetteTrackingNumber:
          status === VignetteStatus.SentByMail ? trackingNumber.trim() || null : null,
        deliveredAt: status === VignetteStatus.NotSent || !deliveredAt ? null : new Date(deliveredAt).toISOString(),
      });
      const rows = await adminService.getMemberVignettes(memberId);
      setHistory(rows);
      onUpdated(updated);
      onSuccess(t('vignette.management.saveSuccess'));
    } catch (err) {
      onError(err instanceof Error ? err.message : t('vignette.management.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    applyRecord(selectedRecord, member);
  };

  const options: {
    value: VignetteStatus;
    tone: 'muted' | 'primary' | 'success';
    icon: React.ReactNode;
    title: string;
    hint: string;
  }[] = [
    {
      value: VignetteStatus.NotSent,
      tone: 'muted',
      icon: <ClockIcon />,
      title: t('vignette.notSent'),
      hint: t('vignette.management.optionHints.notSent'),
    },
    {
      value: VignetteStatus.SentByMail,
      tone: 'primary',
      icon: <EnvelopeIcon />,
      title: t('vignette.sentByMail'),
      hint: t('vignette.management.optionHints.sentByMail'),
    },
    {
      value: VignetteStatus.ReceivedInPerson,
      tone: 'success',
      icon: <HandRaisedIcon />,
      title: t('vignette.receivedInPerson'),
      hint: t('vignette.management.optionHints.receivedInPerson'),
    },
  ];

  return (
    <Panel $embedded={embedded}>
      {!embedded && (
        <Intro>
          <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.section, margin: 0 }}>
            {t('vignette.management.title')}
          </Heading>
          <VignetteStatusBadge status={currentStatus} />
        </Intro>
      )}

      {embedded && (
        <Intro>
          <Text size="sm" color="secondary">
            {t('vignette.management.formIntro')}
          </Text>
          <VignetteStatusBadge status={currentStatus} />
        </Intro>
      )}

      <MetaLine>
        <MetaItem>
          <ClockIcon aria-hidden />
          {t('vignette.lastUpdated')}: {formatUpdatedAt(member.vignetteUpdatedAt)}
        </MetaItem>
        {currentStatus === VignetteStatus.SentByMail && member.vignetteTrackingNumber && (
          <MetaItem>
            <TruckIcon aria-hidden />
            {member.vignetteTrackingNumber}
          </MetaItem>
        )}
      </MetaLine>

      <SectionLabel size="sm" color="text">
        {t('vignette.management.year')}
      </SectionLabel>
      <TrackingInput
        type="number"
        min={2000}
        max={currentYear + 1}
        value={year}
        onChange={e => setYear(parseInt(e.target.value, 10) || currentYear)}
        aria-label={t('vignette.management.year')}
      />

      <SectionLabel size="sm" color="text" style={{ marginTop: spacing[4] }}>
        {t('vignette.management.statusField')}
      </SectionLabel>

      <Options role="radiogroup" aria-label={t('vignette.management.statusField')}>
        {options.map(option => {
          const selected = status === option.value;
          return (
            <OptionCard
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              $selected={selected}
              $tone={option.tone}
              onClick={() => setStatus(option.value)}
            >
              <OptionIcon $tone={option.tone} $selected={selected}>
                {option.icon}
              </OptionIcon>
              <OptionText>
                <OptionTitle>{option.title}</OptionTitle>
                <OptionHint>{option.hint}</OptionHint>
              </OptionText>
              <Radio $selected={selected} aria-hidden />
            </OptionCard>
          );
        })}
      </Options>

      {status !== VignetteStatus.NotSent && (
        <TrackingField>
          <label htmlFor="vignette-delivered">
            <SectionLabel size="sm" color="text">
              {t('vignette.management.deliveredAt')}
            </SectionLabel>
          </label>
          <TrackingInput
            id="vignette-delivered"
            type="date"
            value={deliveredAt}
            onChange={e => setDeliveredAt(e.target.value)}
          />
        </TrackingField>
      )}

      {status === VignetteStatus.SentByMail && (
        <TrackingField>
          <label htmlFor="vignette-tracking">
            <SectionLabel size="sm" color="text">
              {t('vignette.trackingNumberLabel')}
            </SectionLabel>
          </label>
          <TrackingInput
            id="vignette-tracking"
            type="text"
            value={trackingNumber}
            onChange={e => setTrackingNumber(e.target.value)}
            placeholder={t('vignette.trackingNumber')}
          />
          <Text size="xs" color="secondary">
            {t('vignette.management.trackingHint')}
          </Text>
        </TrackingField>
      )}

      {history.length > 0 && (
        <MetaLine>
          <SectionLabel size="sm" color="text" style={{ margin: 0 }}>
            {t('vignette.management.historyTitle')}
          </SectionLabel>
          {history.map(row => (
            <button
              key={row.id}
              type="button"
              onClick={() => setYear(row.year)}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit' }}
            >
              {row.year}
              {row.deliveredAt ? ` · ${row.deliveredAt.slice(0, 10)}` : ''}
            </button>
          ))}
        </MetaLine>
      )}

      <Actions>
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={!dirty || saving}>
          {t('vignette.management.save')}
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={saving}
          type="button"
        >
          {t('common.cancel')}
        </Button>
      </Actions>
    </Panel>
  );
};

export default AdminVignettePanel;
